import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Phone, Mail, Globe, Ticket, Users, Dog, CalendarDays, Heart,
} from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L2 from "leaflet";
import "leaflet/dist/leaflet.css";
import { useLang, L } from "@/i18n";
import { api } from "@/api";
import { SECTION_BY_TYPE, TYPE_COLORS } from "@/config";
import { HeartButton, TagBadge, Reveal, EmptyState } from "@/components/Widgets";
import ItemCard from "@/components/ItemCard";

const dot = (color) =>
  L2.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

export default function Detail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setItem(null);
    setNotFound(false);
    setActiveImg(0);
    api.get(`/items/${slug}`)
      .then((r) => {
        setItem(r.data);
        const rel = r.data.links_items || [];
        const dsh = r.data.links_dishes || [];
        if (rel.length) api.get("/items", { params: { slugs: rel.join(",") } }).then((x) => setRelated(x.data)).catch(() => {});
        else setRelated([]);
        if (dsh.length) api.get("/items", { params: { slugs: dsh.join(",") } }).then((x) => setDishes(x.data)).catch(() => {});
        else setDishes([]);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-24"><EmptyState text={t("common.empty")} /></div>;
  if (!item) return <p className="py-32 text-center text-grafite/50">{t("common.loading")}</p>;

  const color = TYPE_COLORS[item.type] || "#0E6E8C";
  const section = SECTION_BY_TYPE[item.type];
  const relTitle =
    item.type === "dish" ? t("detail.servedIn")
    : item.type === "product" ? t("detail.whereTaste")
    : item.type === "event" ? t("detail.organizedBy")
    : item.type === "offer" ? t("detail.includes")
    : null;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid={`detail-${item.slug}`}>
      <button onClick={() => navigate(-1)} data-testid="detail-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-mare hover:text-terracotta transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Gallery + text */}
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden shadow-[0_16px_50px_rgba(14,110,140,0.15)]">
            <div className="aspect-[4/3] bg-sabbia">
              {item.images?.[activeImg] && (
                <img src={item.images[activeImg]} alt={L(item.title, lang)} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="absolute top-4 right-4"><HeartButton slug={item.slug} /></div>
            {item.category && <div className="absolute top-4 left-4"><TagBadge color={color}>{item.category}</TagBadge></div>}
          </motion.div>
          {item.images?.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto pb-1" data-testid="gallery-thumbs">
              {item.images.map((img, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`shrink-0 w-24 h-16 rounded-2xl overflow-hidden border-2 transition-colors ${i === activeImg ? "border-terracotta" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}

          <Reveal className="mt-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {section && (
                <Link to={section.path} className="text-xs font-bold uppercase tracking-[0.2em] hover:text-terracotta transition-colors" style={{ color }}>
                  {t(section.navKey)}
                </Link>
              )}
              {item.tags?.map((tag) => (
                <span key={tag} className="rounded-full bg-sabbia px-3 py-1 text-xs font-semibold text-grafite/70">{tag}</span>
              ))}
              {item.family && <span className="rounded-full bg-turchese/15 text-mare px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t("common.family")}</span>}
              {item.pets && <span className="rounded-full bg-turchese/15 text-mare px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"><Dog className="w-3 h-3" /> {t("common.pets")}</span>}
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-grafite">{L(item.title, lang)}</h1>
            <p className="mt-4 font-accent text-2xl text-mare leading-snug">{L(item.excerpt, lang)}</p>
            <div className="mt-6 text-grafite/80 text-base sm:text-lg leading-relaxed whitespace-pre-line">{L(item.description, lang)}</div>
            {item.likes > 0 && (
              <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-terracotta">
                <Heart className="w-4 h-4 fill-terracotta" /> {item.likes} like
              </p>
            )}
          </Reveal>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-32 flex flex-col gap-5">
            {item.start && (
              <Reveal className="bg-mare text-calce rounded-3xl p-6" data-testid="detail-dates">
                <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-turchese mb-3 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" /> {t("detail.dates")}
                </h3>
                <p className="font-semibold">{fmtDate(item.start)}</p>
                {item.end && <p className="text-calce/70 text-sm mt-1">→ {fmtDate(item.end)}</p>}
                {item.ticket_link && (
                  <a href={item.ticket_link} target="_blank" rel="noreferrer" data-testid="detail-tickets"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-[#d15f2d] text-white font-bold px-5 py-2.5 text-sm transition-colors">
                    <Ticket className="w-4 h-4" /> {t("common.tickets")}
                  </a>
                )}
              </Reveal>
            )}

            <Reveal className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(14,110,140,0.08)]" data-testid="detail-info">
              <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta mb-4">{t("detail.contacts")}</h3>
              <div className="flex flex-col gap-3 text-sm">
                {item.address && (
                  <p className="flex items-start gap-3 text-grafite/80">
                    <MapPin className="w-4 h-4 mt-0.5 text-mare shrink-0" />
                    <span>{item.address}{item.zone ? ` · ${item.zone}` : ""}</span>
                  </p>
                )}
                {item.phone && (
                  <a href={`tel:${item.phone}`} data-testid="detail-phone" className="flex items-center gap-3 font-semibold text-grafite hover:text-mare transition-colors">
                    <Phone className="w-4 h-4 text-mare" /> {item.phone}
                  </a>
                )}
                {item.email && (
                  <a href={`mailto:${item.email}`} data-testid="detail-email" className="flex items-center gap-3 text-grafite hover:text-mare transition-colors break-all">
                    <Mail className="w-4 h-4 text-mare shrink-0" /> {item.email}
                  </a>
                )}
                {item.website && (
                  <a href={item.website} target="_blank" rel="noreferrer" data-testid="detail-website" className="flex items-center gap-3 font-semibold text-mare hover:text-terracotta transition-colors">
                    <Globe className="w-4 h-4" /> {t("common.website")}
                  </a>
                )}
                {item.price_range && (
                  <p className="flex items-center gap-3 text-grafite/80">
                    <span className="rounded-full bg-sabbia px-3 py-1 text-xs font-bold">{t("common.price")}: {item.price_range}</span>
                  </p>
                )}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {item.phone && (
                  <a href={`tel:${item.phone}`} data-testid="detail-cta-call"
                    className="flex-1 text-center rounded-full bg-terracotta hover:bg-[#d15f2d] text-white font-bold px-5 py-3 text-sm transition-colors">
                    {item.type === "restaurant" || item.type === "accommodation" ? t("common.book") : t("common.call")}
                  </a>
                )}
                {item.lat && item.lng && (
                  <a href={`https://www.google.com/maps?q=${item.lat},${item.lng}`} target="_blank" rel="noreferrer" data-testid="detail-cta-map"
                    className="flex-1 text-center rounded-full border-2 border-mare text-mare hover:bg-mare hover:text-white font-bold px-5 py-3 text-sm transition-colors">
                    {t("detail.openMap")}
                  </a>
                )}
              </div>
            </Reveal>

            {item.lat && item.lng && (
              <Reveal className="rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(14,110,140,0.08)] h-64" data-testid="detail-map">
                <MapContainer center={[item.lat, item.lng]} zoom={14} scrollWheelZoom={false} className="h-full w-full">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                  <Marker position={[item.lat, item.lng]} icon={dot(color)} />
                </MapContainer>
              </Reveal>
            )}
          </div>
        </aside>
      </div>

      {/* Cross links */}
      {dishes.length > 0 && (
        <section className="mt-20" data-testid="related-dishes">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-grafite mb-8">{t("detail.inMenu")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dishes.map((d, i) => <ItemCard key={d.slug} item={d} index={i} />)}
          </div>
        </section>
      )}
      {related.length > 0 && relTitle && (
        <section className="mt-20" data-testid="related-items">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-grafite mb-8">{relTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((r, i) => <ItemCard key={r.slug} item={r} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
